import {inject, Injectable} from "@angular/core";
import {LoginService} from "../login.service";
import {forkJoin, from, Observable, of, shareReplay, throwError} from "rxjs";
import {APIResponse} from "../../models/Response";
import {Organization} from "../../models/organization";
import {catchError, finalize, map, switchMap} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {EntityMember, UserDetails} from "../../models/userDetails";
import {SharedService} from "../shared.service";
import {
    CustomDropdownOption,
    DropDownOption, ProfileFetchTyp,
    ProfileFetchType,
} from "../../modules/shared/Models/shared-data-models.model";
import {ChatUser} from "../../modules/conversation/models/message.model";
import {SecureStorageService} from "../../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../../local-storage-related-data.model";

export interface ImageResult {
    email: string;
    imageUrl: string;
}

interface CachedImage {
    key: string;
    base64Image: string;
    expireDate: string;
}

interface ImageCacheConfig {
    readonly limit: number;
    readonly user_profiles: string; // db name for storing user profile images
    readonly expirationDays: number;
    readonly version: number;
    readonly blacklistStoreName: string; // store name for blacklist (406/403/404)
    readonly versionForBlacklist: number; // optional version for blacklist store
}

@Injectable({
    providedIn: "root",
})
export class UserDetailsService {
    apiServerUrl = environment.apiBaseUrl;
    private readonly cacheConfig: ImageCacheConfig = {
        limit: 500,
        user_profiles: "userProfileImageDB",
        expirationDays: 2,
        version: 3,
        blacklistStoreName: "blacklist406",
        versionForBlacklist: 1,
    };

    //Track active requests to prevent duplicates
    private activeRequests = new Map<string, Observable<Blob>>();

    // Simple 406 blacklist
    private blacklist406 = new Set<string>();  // Just a Set of "userId_profileType"
    private readonly BLACKLIST_406_DAYS = 7;   // Block for 7 days

    // In-memory cache for fast synchronous lookups (avoids async IndexedDB on every call)
    private memoryCache = new Map<string, string>(); // key -> base64
    private memoryCacheOrder: string[] = []; // LRU tracking for eviction
    private readonly MEMORY_CACHE_LIMIT = 150;

    private secureStorageService = inject(SecureStorageService)

    constructor(
        private loginService: LoginService,
        private http: HttpClient,
        private sharedServices: SharedService,
    ) {
        // Load 406 blacklist on startup
        this.load406Blacklist().then(r => {});
    }


    private async add406ToBlacklist(key: string): Promise<void> {
        // Add to memory
        this.blacklist406.add(key);
        // Save to IndexedDB (simple structure)
        const request = indexedDB.open(this.cacheConfig.blacklistStoreName, this.cacheConfig.versionForBlacklist);
        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            const db = (event.target as IDBOpenDBRequest).result;
            // Create simple blacklist store if doesn't exist
            if (!db.objectStoreNames.contains("blacklist406")) {
                db.createObjectStore("blacklist406", { keyPath: "key" });
            }
        };

        request.onsuccess = (event: Event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            try {
                const transaction = db.transaction(["blacklist406"], "readwrite");
                const store = transaction.objectStore("blacklist406");

                const expiry = new Date();
                expiry.setDate(expiry.getDate() + this.BLACKLIST_406_DAYS);

                // Super simple object
                store.put({
                    key: key,
                    expires: expiry.toISOString()
                });

                transaction.oncomplete = () => {
                    console.log(`Blacklisted 406: ${key}`);
                    db.close();
                };
            } catch (error) {
                db.close();
            }
        };
    }


    private async load406Blacklist(): Promise<void> {
        const request = indexedDB.open(this.cacheConfig.blacklistStoreName, this.cacheConfig.versionForBlacklist);
        request.onsuccess = (event: Event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains("blacklist406")) {
                db.close();
                return;
            }

            try {
                const transaction = db.transaction(["blacklist406"], "readonly");
                const store = transaction.objectStore("blacklist406");
                const getAllRequest = store.getAll();

                getAllRequest.onsuccess = () => {
                    const now = new Date().toISOString();
                    const entries = getAllRequest.result || [];

                    // Load non-expired entries
                    entries.forEach((entry: any) => {
                        if (entry.expires > now) {
                            this.blacklist406.add(entry.key);
                        }
                    });

                    console.log(`Loaded ${this.blacklist406.size} blacklisted 406 entries`);
                };

                transaction.oncomplete = () => db.close();
            } catch (error) {
                db.close();
            }
        };
    }

    /**
     * Adds an image to the in-memory cache with LRU eviction.
     * Memory cache provides instant synchronous lookups, avoiding async IndexedDB operations.
     */
    private addToMemoryCache(key: string, value: string): void {
        // Skip invalid values (empty strings or too small to be valid images)
        if (!value || value.length < 100) return;

        // Remove from order if exists (will be moved to end)
        const existingIndex = this.memoryCacheOrder.indexOf(key);
        if (existingIndex > -1) {
            this.memoryCacheOrder.splice(existingIndex, 1);
        }

        // Add to end (most recently used)
        this.memoryCacheOrder.push(key);
        this.memoryCache.set(key, value);

        // Evict oldest entries if over limit (LRU eviction)
        while (this.memoryCacheOrder.length > this.MEMORY_CACHE_LIMIT) {
            const oldestKey = this.memoryCacheOrder.shift();
            if (oldestKey) {
                this.memoryCache.delete(oldestKey);
            }
        }
    }

    getProfileImage(
        getImageId: string,
        getProfileType: ProfileFetchTyp = ProfileFetchType.USER_EMAIL
    ): Observable<Blob> {
        const cacheKey = `${getImageId}_${getProfileType}`;

        // 1. Check active requests (concurrent call deduplication)
        if (this.activeRequests.has(cacheKey)) {
            return this.activeRequests.get(cacheKey)!;
        }

        // 2. Check blacklist (skip known 406/403/404 errors)
        if (this.blacklist406.has(cacheKey)) {
            return of(new Blob([]));
        }

        // 3. Check MEMORY cache FIRST (synchronous, instant - no async operations)
        const memoryCached = this.memoryCache.get(cacheKey);
        if (memoryCached && memoryCached.length > 100) {
            // Update LRU order (move to most recently used)
            const idx = this.memoryCacheOrder.indexOf(cacheKey);
            if (idx > -1) {
                this.memoryCacheOrder.splice(idx, 1);
                this.memoryCacheOrder.push(cacheKey);
            }
            return of(this.base64ToBlob(memoryCached));
        }

        // 4. Check IndexedDB (async) then fallback to API
        const request$ = from(this.getFromImageIndexedDb(cacheKey)).pipe(
            switchMap((cachedImage) => {
                if (cachedImage) {
                    // Populate memory cache on IndexedDB hit for faster subsequent lookups
                    this.blobToBase64(cachedImage).then(base64 => {
                        this.addToMemoryCache(cacheKey, base64);
                    });
                    return of(cachedImage);
                }
                return this.getProfileImageAPI(getImageId, getProfileType);
            }),
            shareReplay(1),
            finalize(() => {
                this.activeRequests.delete(cacheKey);
            })
        );

        this.activeRequests.set(cacheKey, request$);
        return request$;
    }

    getProfileImageAPI(getImageId: string, getProfileType: number) {
        let endpoint: string;
        switch (getProfileType) {
            case 1:
                endpoint = `${this.apiServerUrl}/register/getUserImageByAccountId/${Number(getImageId)}`;
                break;
            case 2:
                endpoint = `${this.apiServerUrl}/register/getUserImageByEmail/${getImageId}`;
                break;
            default:
                endpoint = `${this.apiServerUrl}/register/getUserImageByUserId/${getImageId}`;
                break;
        }
        const accountIds = this.sharedServices.getAllAccountIdsOfLoggedInUser(true);

        return this.http
            .get<Blob>(endpoint, {
                headers: {
                    accountIds: accountIds.toString(),
                },
                responseType: "blob" as "json",
            })
            .pipe(
                // Chain the save properly instead of fire-and-forget
                switchMap((blob) => {
                    // Don't cache empty blobs
                    if (!blob || blob.size === 0) {
                        return of(blob);
                    }

                    const cacheKey = `${getImageId}_${getProfileType}`;

                    return from(this.blobToBase64(blob)).pipe(
                        switchMap((base64) => {
                            // Only cache valid image data
                            if (base64 && base64.startsWith('data:image/') && base64.length > 100) {
                                // Save to memory cache immediately (synchronous)
                                this.addToMemoryCache(cacheKey, base64);

                                // Save to IndexedDB (chained, waits for completion)
                                return from(this.saveImageToIndexedDBAsync(cacheKey, base64)).pipe(
                                    map(() => blob),
                                    catchError(() => of(blob)) // If save fails, still return blob
                                );
                            }
                            return of(blob);
                        })
                    );
                }),
                catchError((error: any) => {
                    // Handle 406/403/404 errors by blacklisting
                    if (error.status === 406 || error.status === 403 || error.status === 404) {
                        const key = `${getImageId}_${getProfileType}`;
                        this.add406ToBlacklist(key);
                    }
                    return of(new Blob([]));
                }),
            );
    }

    private getFromImageIndexedDb(key: string): Promise<Blob | null> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.cacheConfig.user_profiles, this.cacheConfig.version);
            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains("images")) {
                    // Use 'key' as the keyPath
                    db.createObjectStore("images", { keyPath: "key" });
                }
            };

            request.onsuccess = (event: Event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                try {
                    const transaction = db.transaction(["images"], "readwrite");
                    const imagesStore = transaction.objectStore("images");
                    // Get the image by the full key
                    const getRequest = imagesStore.get(key);

                    getRequest.onsuccess = () => {
                        const result = getRequest.result as CachedImage | undefined;
                        // Check for valid base64 data (length > 100 ensures it's not empty or invalid)
                        if (result && result.base64Image && result.base64Image.length > 100) {
                            if (result.expireDate < new Date().toISOString()) {
                                // Delete by the full key
                                const deleteRequest = imagesStore.delete(key);
                                deleteRequest.onsuccess = () => {};
                                resolve(null);
                            } else {
                                resolve(this.base64ToBlob(result.base64Image));
                            }
                        } else {
                            resolve(null);
                        }
                    };
                    transaction.oncomplete = () => db.close();
                    getRequest.onerror = () => {
                        db.close();
                        resolve(null);
                    };
                } catch (e) {
                    db.close();
                    reject(`Error retrieving image from IndexedDB: ${e}`);
                }
            };
            request.onerror = () => resolve(null);
        });
    }

    private saveImageToIndexedDB(key: string, base64Image: string): void {
        const request = indexedDB.open(this.cacheConfig.user_profiles, this.cacheConfig.version);
        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains("images")) {
                // Use 'key' as the keyPath
                db.createObjectStore("images", { keyPath: "key" });
            }
        };

        request.onsuccess = (event: Event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            try {
                const transaction = db.transaction(["images"], "readwrite");
                const imagesStore = transaction.objectStore("images");
                const expireDate = new Date();
                expireDate.setDate(expireDate.getDate() + this.cacheConfig.expirationDays);

                const imageRecord: CachedImage = {
                    key, // Use the composite key here
                    base64Image,
                    expireDate: expireDate.toISOString()
                };
                imagesStore.put(imageRecord);
                this.ensureCacheLimit(db);
                transaction.oncomplete = () => {
                    // console.log("Image saved to IndexedDB for key:", key);
                    db.close();
                };
            } catch (error) {
                db.close();
            }
        };
    }

    /**
     * Async version of saveImageToIndexedDB that returns a Promise.
     * This allows proper chaining in the observable pipeline to prevent race conditions.
     */
    private saveImageToIndexedDBAsync(key: string, base64Image: string): Promise<void> {
        return new Promise((resolve) => {
            const request = indexedDB.open(this.cacheConfig.user_profiles, this.cacheConfig.version);

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains("images")) {
                    db.createObjectStore("images", { keyPath: "key" });
                }
            };

            request.onsuccess = (event: Event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                try {
                    const transaction = db.transaction(["images"], "readwrite");
                    const imagesStore = transaction.objectStore("images");
                    const expireDate = new Date();
                    expireDate.setDate(expireDate.getDate() + this.cacheConfig.expirationDays);

                    const imageRecord: CachedImage = {
                        key,
                        base64Image,
                        expireDate: expireDate.toISOString()
                    };
                    imagesStore.put(imageRecord);
                    this.ensureCacheLimit(db);

                    transaction.oncomplete = () => {
                        db.close();
                        resolve();
                    };
                    transaction.onerror = () => {
                        db.close();
                        resolve(); // Resolve anyway to not break the flow
                    };
                } catch (error) {
                    db.close();
                    resolve(); // Resolve anyway to not break the flow
                }
            };

            request.onerror = () => resolve(); // Resolve anyway to not break the flow
        });
    }

    private ensureCacheLimit(db: IDBDatabase): void {
        const transaction = db.transaction("images", "readwrite");
        const imagesStore = transaction.objectStore("images");
        const getAllRequest = imagesStore.getAll();
        getAllRequest.onsuccess = (event: Event) => {
            const images = (event.target as IDBRequest<CachedImage[]>).result;
            if (images.length > this.cacheConfig.limit) {
                // Sort images by expireDate to find the oldest one
                images.sort((a, b) => new Date(a.expireDate).getTime() - new Date(b.expireDate).getTime());
                const oldestImage = images[0]; // Now this is correctly the oldest
                const deleteRequest = imagesStore.delete(oldestImage.key); // Use the correct key property

                deleteRequest.onsuccess = () => {
                    console.log(`Cache limit exceeded. Removed oldest image: ${oldestImage.key}`);
                };
                deleteRequest.onerror = (err: any) => {
                    console.error("Error removing the oldest image:", err.target.error);
                };
            }
        };
        getAllRequest.onerror = () => {
            console.error("Error fetching images from IndexedDB to check cache limit");
        };
    }

    // Convert Blob to Base64
    blobToBase64(blob: Blob): Promise<string> {
        // Handle null, undefined or empty blob
        if (!blob || blob.size === 0) {
            return Promise.resolve("");
        }

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    // Convert Base64 to Blob
    private base64ToBlob(base64: string): Blob {
        const [prefix, base64Data] = base64.split(",");
        const binaryString = atob(base64Data); // Decode Base64 to binary string
        const byteArrays = [];

        for (let offset = 0; offset < binaryString.length; offset += 1024) {
            const slice = binaryString.slice(offset, offset + 1024);
            const byteArray = new Uint8Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteArray[i] = slice.charCodeAt(i);
            }
            byteArrays.push(byteArray);
        }

        // Dynamic MIME type based on image file type
        const mimeType = prefix.split(";")[0].split(":")[1]; // Extract MIME type from Base64 string
        return new Blob(byteArrays, {type: mimeType});
    }


    // Other methods...

    isUserDetails(user: UserDetails | ChatUser): user is ChatUser {
        return "userId" in user;
    }

    convertUserDetailsListIntoCustomDropDownOptionWithoutProfileImageUrl(users: UserDetails[]) {
        return users.map((user: UserDetails) => {
            let name = user.firstName + " " + user.lastName;
            return {
                value: user.accountId,
                label: name,
                toolTip: user.email,
                userNameLabel: user.email,
                profileImage: "",
                isDisabled: user.isActive == undefined ? false : !user.isActive,
            } as CustomDropdownOption;
        });
    }

    // function to convert UserDetails[] into CustomDropDown[] with profile image
    convertUserDetailsListIntoCustomDropDownOptionWithProfileImageUrl(users: UserDetails[]) {
        return users.map((user: UserDetails) => {
            let name = user.firstName + " " + user.lastName;
            return {
                value: user.accountId,
                label: name,
                toolTip: user.email,
                userNameLabel: user.email,
                profileImage: user.profileImage,
                isDisabled: user.isActive == undefined ? false : !user.isActive,
            } as CustomDropdownOption;
        });
    }

    formatSelectOptionsAndUserProfileImage(
        usersToAssignTask: UserDetails[] | ChatUser[],
        isChatUser: boolean = false,
    ): Promise<CustomDropdownOption[]> {
        return new Promise((resolve, reject) => {
            if (!usersToAssignTask || usersToAssignTask.length === 0) {
                // Return immediately with empty dropdown options
                return resolve([]);
            }

            const imageRequests = usersToAssignTask.map((user) =>
                this.getProfileImage(user.email, ProfileFetchType.USER_EMAIL).pipe(
                    switchMap((blob: Blob) => from(this.blobToBase64(blob))),
                    catchError(() => from([null])),
                ),
            );

            forkJoin(imageRequests).subscribe({
                next: (imageUrls: (string | null)[]) => {
                    const usersToAssignTaskOptions: CustomDropdownOption[] = usersToAssignTask.map((user, index) => {
                        let name: string = `${user.firstName} ${user.lastName}`;
                        if (this.isUserDetails(user) && isChatUser) {
                            return {
                                value: user.userId,
                                label: name,
                                toolTip: user.email,
                                userNameLabel: user.email,
                                profileImage: imageUrls[index],
                                extras: user,
                                userId: user.userId,
                            };
                        } else {
                            return {
                                value: user.accountId,
                                label: name,
                                toolTip: user.email,
                                userNameLabel: user.email,
                                profileImage: imageUrls[index],
                                isDisabled: user.isActive === undefined ? false : !user.isActive,
                            };
                        }
                    });
                    resolve(usersToAssignTaskOptions);
                },
                error: (err) => {
                    console.error("Error fetching profile images:", err);
                    reject(err);
                },
            });
        });
    }

    formatSelectOptionsAndUserProfileImageValueEmail(
        usersToAssignTask: UserDetails[],
    ): Promise<CustomDropdownOption[]> {
        return new Promise((resolve, reject) => {
            const imageRequests = usersToAssignTask.map((user) =>
                this.getProfileImage(user.email, ProfileFetchType.USER_EMAIL).pipe(
                    switchMap(
                        (blob: Blob) => from(this.blobToBase64(blob)), // Convert the Promise to an Observable
                    ),
                    catchError(() => {
                        return from([null]);
                    }),
                ),
            );

            forkJoin(imageRequests).subscribe({
                next: (imageUrls: (string | null)[]) => {
                    const usersToAssignTaskOptions: CustomDropdownOption[] = usersToAssignTask.map((user, index) => {
                        let name = user.firstName + " " + user.lastName;
                        return {
                            value: user.email,
                            label: name,
                            toolTip: user.email,
                            userNameLabel: user.email,
                            profileImage: imageUrls[index],
                            isDisabled: user.isActive === undefined ? false : !user.isActive,
                        };
                    });
                    resolve(usersToAssignTaskOptions);
                },
                error: (err) => {
                    console.error("Error fetching profile images:", err);
                    reject(err);
                },
            });
        });
    }

    /**
     * Creates a unique map of users by email to avoid duplicates
     * @param users Array of user details
     * @returns Map with unique users by email
     */
    static createUniqueUsersMap<T extends { email: string }>(users: T[]): Map<string, T> {
        const uniqueUsersMap = new Map<string, T>();
        users.forEach((user) => {
            if (!uniqueUsersMap.has(user.email)) {
                uniqueUsersMap.set(user.email, user);
            }
        });
        return uniqueUsersMap;
    }

    /**
     * Creates observable requests for fetching profile images
     * This is the core reusable method for creating image observables
     * @param users Array of users for whom to fetch images
     * @returns Array of observables for image fetching
     */
    public createProfileImageObservables<T extends { email: string }>(users: T[]): Observable<ImageResult>[] {
        return users.map(user =>
            this.getProfileImage(user.email, ProfileFetchType.USER_EMAIL)
                .pipe(
                    switchMap((blob: Blob) => from(this.blobToBase64(blob))),
                    map((imageUrl: string): ImageResult => ({email: user.email, imageUrl})),
                    catchError((err): Observable<ImageResult> => {
                        console.error(`Error fetching image for user ${user.email}:`, err);
                        return of({email: user.email, imageUrl: ''});
                    })
                )
        );
    }

    /**
     * Formats the options for entity members to be used in a dropdown.(Without profile image)
     * @param usersToAssignTask
     */
    formatOptionsForEntityMember(usersToAssignTask: EntityMember[]) {
        if (!usersToAssignTask || usersToAssignTask.length === 0) {
            // Return immediately with empty dropdown options
            return [] as DropDownOption<any>[];
        }

        const usersToAssignTaskOptions: DropDownOption<any>[] = usersToAssignTask.map((user) => {
            let name = user.firstName + " " + user.lastName;
            return {
                value: user,
                label: name,
                secondary_label: user.email.split("@")[0],
                toolTip: user.email,
                userNameLabel: user.email,
                isInactive: user.isActive === undefined ? false : !user.isActive,
                avatar: '', // No profile image
            };
        });
        return usersToAssignTaskOptions;
    }

    /**
     * @params userID: string
     * @description: UserId can be user email and user id/account id.
     * */

    removeUserImageFromDB(userID: string) {
        const request = indexedDB.open(this.cacheConfig.user_profiles, this.cacheConfig.version);
        request.onsuccess = (event: any) => {
            const db = event.target.result;
            const transaction = db.transaction(["images"], "readwrite");
            const imagesStore = transaction.objectStore("images");

            const deleteRequest = imagesStore.delete(userID);

            deleteRequest.onsuccess = () => {
                console.log("Image removed from IDB.");
            };

            deleteRequest.onerror = (err: any) => {
                console.error("Error removing image from IDB:", err.target.error);
            };
        };

        request.onerror = (event: any) => {
            console.error("Error opening IDB:", event.target.error);
        };
    }

    formatOptionsAndUserProfileImageValueForEntityMember(
        usersToAssignTask: EntityMember[] | UserDetails[],
        isUserDetailsType: boolean = false,
    ): Promise<DropDownOption<any>[]> {
        return new Promise((resolve, reject) => {
            const imageRequests = usersToAssignTask.map((user) =>
                this.getProfileImage(user.email, ProfileFetchType.USER_EMAIL).pipe(
                    switchMap(
                        (blob: Blob) => from(this.blobToBase64(blob)), // Convert the Promise to an Observable
                    ),
                    catchError(() => {
                        return from([null]);
                    }),
                ),
            );

            forkJoin(imageRequests).subscribe({
                next: (imageUrls: (string | null)[]) => {
                    if (!isUserDetailsType) {
                        const usersToAssignTaskOptions: DropDownOption<any>[] = usersToAssignTask.map((user, index) => {
                            let name = user.firstName + " " + user.lastName;
                            return {
                                value: user,
                                label: name,
                                secondary_label: user.email.split("@")[0],
                                toolTip: user.email,
                                userNameLabel: user.email,
                                avatar: imageUrls[index],
                                isInactive: user.isActive === undefined ? false : !user.isActive,
                            };
                        });
                        resolve(usersToAssignTaskOptions);
                    } else {
                        const usersToAssignTaskOptions: DropDownOption<any>[] = usersToAssignTask.map((user, index) => {
                            let name = user.firstName + " " + user.lastName;
                            return {
                                value: user,
                                label: name,
                                label_extended: (user as UserDetails).org,
                                secondary_label: user.email.split("@")[0],
                                toolTip: user.email,
                                userNameLabel: user.email,
                                avatar: imageUrls[index],
                                isInactive: user.isActive === undefined ? false : !user.isActive,
                            };
                        });
                        resolve(usersToAssignTaskOptions);
                    }
                },
                error: (err) => {
                    console.error("Error fetching profile images:", err);
                    reject(err);
                },
            });
        });
    }

    getUserEmailFromToken(): string | null {
        let token: any = this.secureStorageService.getItem(LocalStorageRelatedData.TOKEN) || null;
        if (token) {
            let decodedToken: any = this.loginService.getDecodedAccessToken(token);
            return this.loginService.getTokenEmail(decodedToken);
        } else {
            return null;
        }
    }

    getOrganizations(): Observable<APIResponse<Organization[]>> {
        return this.http
            .get<APIResponse<Organization[]>>(`${this.apiServerUrl}/organization/userAllOrganizationList`)
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    //     /api/accessDomain/getEntityMembers/{entityTypeId/entityId}
    getEntityMembers(
        entityTypeId: number,
        entityId: number,
        accountIds: string,
    ): Observable<APIResponse<UserDetails[]>> {
        return this.http
            .get<APIResponse<UserDetails[]>>(
                `${this.apiServerUrl}/accessDomain/getEntityMembers/${entityTypeId}/${entityId}`,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    //     /api/accessDomain/getAllEntityMembers/{entityTypeId}/{entityId}
    getAllEntityMembers(
        entityTypeId: number,
        entityId: number,
        accountIds: string,
    ): Observable<APIResponse<EntityMember[]>> {
        return this.http
            .get<APIResponse<EntityMember[]>>(
                `${this.apiServerUrl}/accessDomain/getAllEntityMembers/${entityTypeId}/${entityId}`,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }
}
